import { authSplitApi } from 'redux/helpers/slice.helpers';
import { EPolicyUrls } from 'data/urls/policy.url';
import { TSignatureType } from 'data/types/GeneralTypes';
import {
  TCreateSuggestionUI,
  TEditSuggestionUI,
  TSuggestionTextUI,
} from 'data/types/Policy.types';

const convertDocTypeToUrl = (docType?: TSignatureType | null) => {
  if (!docType) {
    return 'general';
  }
  if (docType === 'SERVICE_FORM') {
    return 'sa';
  }
  return docType.replace('_FORM', '').toLocaleLowerCase();
};

export const policyApi = authSplitApi('policy', [
  'esaDoc',
  'suggestions',
]).injectEndpoints({
  endpoints: build => ({
    getDocHtml: build.query<
      Blob,
      {
        signedDocType?: TSignatureType | null;
        policyId: string | null;
        tenantName: string;
      }
    >({
      query({ signedDocType, policyId, tenantName }) {
        const url = convertDocTypeToUrl(signedDocType);
        return {
          url: `${EPolicyUrls.POLICY}/${EPolicyUrls.DOC}/${url}`,
          method: 'GET',
          params: { policyId, tenantName },
          responseHandler: async response => {
            const text = await response.text();
            return new Blob([text], { type: 'text/html' });
          },
        };
      },
    }),
    getTemplates: build.query<
      { docType: string; preSignedUrl: string }[],
      void
    >({
      query() {
        return {
          url: `${EPolicyUrls.APPLICATION_SAMPLE_TEMPLATE}`,
          method: 'GET',
        };
      },
    }),
    uploadTemplate: build.mutation<
      { docType: string; preSignedUrl: string }[],
      {
        doc: {
          bucket: string;
          key: string;
          level: string;
          region: string;
          identityId: string;
        };
        docType: string;
      }
    >({
      query(body) {
        return {
          url: `${EPolicyUrls.APPLICATION_SAMPLE_TEMPLATE}`,
          method: 'POST',
          body,
        };
      },
    }),
    editSuggestion: build.mutation<void, TEditSuggestionUI>({
      query(body) {
        return {
          url: `${EPolicyUrls.POLICY_CONTENT_SUGGESTION}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['suggestions'],
    }),
    createSuggestion: build.mutation<void, TCreateSuggestionUI>({
      query(body) {
        return {
          url: `${EPolicyUrls.POLICY_CONTENT_SUGGESTION}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['suggestions'],
    }),
    deleteSuggestion: build.mutation<
      void,
      { policyContentSuggestionId: string }
    >({
      query(body) {
        return {
          url: `${EPolicyUrls.POLICY_CONTENT_SUGGESTION}`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: ['suggestions'],
    }),
    getSuggestions: build.query<TSuggestionTextUI[], void>({
      query() {
        return {
          url: `${EPolicyUrls.POLICY_CONTENT_SUGGESTION}`,
          method: 'GET',
        };
      },
      providesTags: ['suggestions'],
    }),
  }),
});

export const {
  useGetDocHtmlQuery,
  useGetTemplatesQuery,
  useUploadTemplateMutation,
  useEditSuggestionMutation,
  useCreateSuggestionMutation,
  useDeleteSuggestionMutation,
  useGetSuggestionsQuery,
} = policyApi;
